import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { EstadoCargando, EstadoPendiente } from '@xiltrion/ui/bloques/estados';
import { ApiError, API_BASE, apiGet, forgetTenant, TENANT } from '@/api/client';

/** `GET /auth/providers`: si el ingreso con la suite está configurado y dónde está su portada. */
interface Providers {
  /** Ingreso con la suite Datalog (GUIA-AGENTES.md §3.1 de la suite): el único de la web. */
  oidc: boolean;
  /** Portada de la suite (`SUITE_PORTAL` o el origen de `OIDC_ISSUER`); `null` sin suite. */
  portal: string | null;
}

/** `GET /auth/oidc/start` con lo que este navegador sabe: la entidad (si la conoce) y la ruta pedida. */
function suiteLoginUrl(params: URLSearchParams): string {
  const q = new URLSearchParams();
  if (TENANT) q.set('tenant', TENANT);
  const volver = params.get('volver');
  if (volver && volver.startsWith('/') && !volver.startsWith('//')) q.set('volver', volver);
  const qs = q.toString();
  return `${API_BASE}/auth/oidc/start${qs ? `?${qs}` : ''}`;
}

/** Portada de la suite con el código del error (GUIA §3.1 «Vuelta con error»): lo explica y ofrece entrar otra vez. */
function suiteErrorUrl(portal: string, error: string): string {
  const q = new URLSearchParams();
  if (TENANT) q.set('entidad', TENANT);
  q.set('app', 'planning');
  q.set('error', error);
  return `${portal}/ingreso?${q.toString()}`;
}

/** La página se va una sola vez, aunque el efecto corra de nuevo. */
let gone = false;
const go = (url: string) => {
  if (gone) return;
  gone = true;
  window.location.replace(url);
};

/**
 * El ingreso es el de la suite Datalog (GUIA-AGENTES.md §3.1 «Sin pantalla propia» y §3.7 de la suite): sin sesión, la
 * web va directo a `GET /auth/oidc/start` del API (con `EstadoCargando` mientras tanto); quien ya está en el portal
 * vuelve sin ver nada y quien no, ve la portada de la suite con la cortina de Planning abierta. Planning no tiene
 * pantalla de ingreso ni de error: un `?error=` va a la portada de la suite con el código, y una entidad guardada que
 * Planning no conoce se olvida (la suite decide cuál). Solo se queda quieta si este API no tiene la suite configurada.
 */
export function Ingreso() {
  const [params] = useSearchParams();
  const error = params.get('error');
  const { data: providers, error: failed, isPending } = useQuery({
    queryKey: ['providers'],
    queryFn: () => apiGet<Providers>('/auth/providers'),
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });
  const tenantMissing = failed instanceof ApiError && failed.code === 'TENANT_NOT_FOUND';
  const notConfigured = !!providers && !providers.oidc;

  useEffect(() => {
    if (isPending || notConfigured) return;
    if (tenantMissing) forgetTenant();
    if (error && providers?.portal) go(suiteErrorUrl(providers.portal, error));
    // Sin `providers` (entidad desconocida o el API no respondió) decide el inicio OIDC: la web nunca se queda esperando.
    else go(suiteLoginUrl(params));
  }, [isPending, notConfigured, tenantMissing, error, providers, params]);

  if (notConfigured) {
    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <EstadoPendiente icono="mdi:apps" titulo="Planning entra con la suite Datalog" descripcion="Este API no tiene configurado el ingreso con la suite (OIDC_ISSUER y OIDC_CLIENT_ID). Configúrala y entra desde su portal." />
      </div>
    );
  }
  return <div className="p-10" aria-busy="true"><EstadoCargando /></div>;
}
